import type { EditableConfigSpec } from '@/api/console';

/**
 * EAA-1823: parsing and formatting between the config editor's text inputs and
 * the REAL JSON types `POST /admin/config/stage` demands.
 *
 * The server type-checks strictly and on purpose: it rejects `"7"` where it
 * declared `int`, and rejects `1` where it declared `bool` (see
 * `RuntimeConfigService.validate_editable`). A text input yields a string, so
 * something has to convert — doing it here, in one tested place, keeps every
 * editor from inventing its own coercion and shipping `"true"` to a bool key.
 */

/** Why a parse failed. Each maps to a `console.config.parseError.*` string. */
export type ParseErrorCode =
    | 'notAnInteger'
    | 'outOfRange'
    | 'tooLong'
    | 'notAUrl'
    | 'notAnOrigin'
    | 'tooManyItems';

/**
 * A parse either produced a value, or a reason to show the user.
 *
 * Written as ONE shape with optional members rather than the usual discriminated
 * union of `{ok: true, value}` | `{ok: false, error}`: this workspace compiles
 * with `strict: false` (see `tsconfig.base.json`), and without `strictNullChecks`
 * TypeScript does not narrow a union on a boolean discriminant — `if (!parsed.ok)`
 * leaves `parsed` as the full union, so reading `parsed.error` is an error even
 * though it is correct at runtime. A flat shape is honest about what the compiler
 * here can actually check.
 */
export interface ParseResult {
    ok: boolean;
    value?: unknown;
    error?: ParseErrorCode;
}

/**
 * Render a value into the form the editor's text input should start from.
 *
 * Distinct from `displayValue` in `configDisplay.ts` on purpose: this one must
 * ROUND-TRIP — whatever it produces has to parse back to the same value — so it
 * cannot say "not set" or "1 hour". `displayValue` is for reading; this is for
 * editing.
 */
export function toEditorText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join('\n');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function isHttpUrl(raw: string): boolean {
    try {
        const parsed = new URL(raw);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * An origin is scheme + host (+ port) and nothing else — the same rule the
 * server applies. Checked with the URL parser rather than a regex so that
 * credentials, paths and query strings are all caught by construction.
 */
function isBareOrigin(raw: string): boolean {
    try {
        const parsed = new URL(raw);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
        if (parsed.username || parsed.password) return false;
        if (parsed.search || parsed.hash) return false;
        // The URL parser normalises a missing path to "/", so both count as bare.
        if (parsed.pathname !== '' && parsed.pathname !== '/') return false;
        // ...but reject a trailing slash the user typed, since the server stores
        // the string verbatim and would refuse it.
        return raw.trim() === parsed.origin;
    } catch {
        return false;
    }
}

/**
 * Turn the editor's raw input into the JSON value to stage, applying the same
 * bounds the server will. Client-side checking here is a courtesy, not the
 * gate — the server re-validates everything and is the real authority.
 */
export function parseValue(spec: EditableConfigSpec, raw: string): ParseResult {
    if (spec.type === 'bool') {
        // The editor for a bool is a select of exactly these two, so anything
        // else cannot be produced by the UI.
        return { ok: true, value: raw === 'true' };
    }

    if (spec.type === 'int') {
        const trimmed = raw.trim();
        // Number() accepts "", "0x10" and "1e3"; require plain digits so the
        // value staged is unambiguously the integer the operator typed.
        if (!/^-?\d+$/.test(trimmed)) return { ok: false, error: 'notAnInteger' };
        const parsed = Number(trimmed);
        if (spec.min !== undefined && parsed < spec.min) return { ok: false, error: 'outOfRange' };
        if (spec.max !== undefined && parsed > spec.max) return { ok: false, error: 'outOfRange' };
        return { ok: true, value: parsed };
    }

    if (spec.type === 'str') {
        const trimmed = raw.trim();
        if (spec.max_length !== undefined && trimmed.length > spec.max_length) {
            return { ok: false, error: 'tooLong' };
        }
        if (spec.format === 'url' && !isHttpUrl(trimmed)) return { ok: false, error: 'notAUrl' };
        return { ok: true, value: trimmed };
    }

    // list: one entry per line, blank lines dropped so a trailing newline is not
    // staged as an empty origin.
    const items = raw
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    if (spec.max_items !== undefined && items.length > spec.max_items) {
        return { ok: false, error: 'tooManyItems' };
    }
    if (spec.item_format === 'origin' && !items.every(isBareOrigin)) {
        return { ok: false, error: 'notAnOrigin' };
    }
    return { ok: true, value: items };
}
